import type { AIAnalysisRequest, AIAnalysisResult } from '$lib/types/ai';

/**
 * AI Utility Service
 * Provides common utility functions for AI analysis operations
 * Single Responsibility: Utility functions and validation
 */
export class AIUtilityService {
  /**
   * Find text position in document
   * Attempts exact match first, then searches nearby area if approximate position given
   */
  static findTextPosition(
    text: string, 
    searchText: string, 
    approximatePosition: number
  ): { start: number, end: number } {
    // Always try exact match in entire text first (most accurate)
    const exactIndex = text.indexOf(searchText);
    if (exactIndex !== -1) {
      return { start: exactIndex, end: exactIndex + searchText.length };
    }
    
    // If not found and we have an approximate position, search nearby
    if (approximatePosition >= 0) {
      const searchRadius = 100;
      const searchStart = Math.max(0, approximatePosition - searchRadius);
      const searchEnd = Math.min(text.length, approximatePosition + searchRadius + searchText.length);
      const searchArea = text.substring(searchStart, searchEnd);
      
      const relativeIndex = searchArea.indexOf(searchText);
      if (relativeIndex !== -1) {
        const start = searchStart + relativeIndex;
        return { start, end: start + searchText.length };
      }
    }
    
    // Not found
    return { start: -1, end: -1 };
  }

  /**
   * Estimate token usage for AI request
   */
  static estimateTokenUsage(request: AIAnalysisRequest): number {
    const basePromptTokens = 500; // System prompt base tokens
    const contextTokens = request.contextText.length / 4; // Rough token estimation
    const correctionsTokens = request.corrections.length * 50; // Average tokens per correction
    
    return Math.ceil(basePromptTokens + contextTokens + correctionsTokens);
  }

  /**
   * Validate AI analysis results
   */
  static validateResults(results: AIAnalysisResult[], expectedCount: number): AIAnalysisResult[] {
    if (!Array.isArray(results)) {
      throw new Error('AI response is not in array format');
    }

    if (results.length !== expectedCount) {
      console.warn(`Expected ${expectedCount} results, got ${results.length}`);
    }

    return results.filter(result => {
      return (
        typeof result.correctionIndex === 'number' &&
        typeof result.selectedValue === 'string' &&
        typeof result.isExceptionProcessed === 'boolean' &&
        typeof result.reasoning === 'string'
      );
    });
  }

  /**
   * Clean JSON response from markdown code blocks
   */
  static cleanJsonResponse(response: string): string {
    let cleanResponse = response;
    
    // Remove markdown code blocks
    if (response.includes('```json')) {
      cleanResponse = response.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
    } else if (response.includes('```')) {
      cleanResponse = response.replace(/```\n?/g, '').trim();
    }
    
    return cleanResponse;
  }

  /**
   * Parse JSON safely with fallback
   */
  static parseJsonSafely<T>(jsonString: string, fallback: T): T {
    try {
      return JSON.parse(jsonString);
    } catch (error) {
      console.warn('Failed to parse JSON:', error);
      return fallback;
    }
  }

  /**
   * Extract context around error position
   */
  static extractErrorContext(
    text: string,
    startPos: number,
    endPos: number,
    contextSize: number = 50
  ): {
    before: string;
    error: string;
    after: string;
  } {
    const beforeStart = Math.max(0, startPos - contextSize);
    const afterEnd = Math.min(text.length, endPos + contextSize);
    
    return {
      before: text.substring(beforeStart, startPos),
      error: text.substring(startPos, endPos),
      after: text.substring(endPos, afterEnd)
    };
  }

  /**
   * Format error position for display
   */
  static formatPosition(start: number, end: number): string {
    return `[${start}:${end}]`;
  }

  /**
   * Repair malformed JSON string
   * Attempts to fix common JSON formatting issues
   */
  static repairJson(jsonString: string): string {
    let repaired = jsonString;
    
    // Fix unescaped quotes in strings
    repaired = repaired.replace(/([^\\])"([^"]*)"([^:,\]\}])/g, '$1\\"$2\\"$3');
    
    // Fix trailing commas
    repaired = repaired.replace(/,(\s*[\]\}])/g, '$1');
    
    // Ensure proper array/object closure
    const openBrackets = (repaired.match(/\[/g) || []).length;
    const closeBrackets = (repaired.match(/\]/g) || []).length;
    const openBraces = (repaired.match(/\{/g) || []).length;
    const closeBraces = (repaired.match(/\}/g) || []).length;
    
    // Add missing closing brackets
    if (openBrackets > closeBrackets) {
      repaired += ']'.repeat(openBrackets - closeBrackets);
    }
    if (openBraces > closeBraces) {
      repaired += '}'.repeat(openBraces - closeBraces);
    }
    
    return repaired;
  }

  /**
   * Calculate progress percentage
   */
  static calculateProgress(current: number, total: number): number {
    if (total <= 0) return 0;
    return Math.min(Math.round((current / total) * 100), 100);
  }

  /**
   * Truncate text for display
   */
  static truncateText(text: string, maxLength: number = 100): string {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength - 3) + '...';
  }
}